import { GoogleSignin } from '@react-native-google-signin/google-signin';
import {
    format,
    toDate
} from 'date-fns';
import { Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { sortBy } from 'underscore';

import googleServices from '../../../../../android/app/google-services.json';
import actions from '../../../../actions';
import { selectOptions } from '../../../../common';
import { getNetworkStatus } from '../../../../hooks/useNetworkStatus';
import { FileUploadService } from '../../../../services/FileUploadService';
import store from '../../../../store';
import {
    decrypt,
    getDuplicates
} from '../../../../utils';
import { extractFileNameFromFullPath } from '../../../../utils/files';
import { getRandomNumberString } from '../../../../utils/strings';
import {
    compareFileName,
    PARTS_DELIMITER
} from '../../fileNameUtil';
import {
    autoBackup,
    backupInfoProcessing,
    DEVICE_FILE_NAME,
    getAttachFileName,
    getDeviceId,
    getFileName,
    USE_ATTACHMENTS
} from '../Backup';
import {
    CLEAR_DELETED_ATTACHES_AFTER_BACKUP,
    clearDeletedAttaches,
    getCurrentState,
    getDeletedAttaches,
    getFullBackupData,
    loadBackupStates,
    restoreData,
    setLastBackupState
} from '../common/backupData';
import { setBackupError } from '../common/errors';
import {
    convertToDocPath,
    isExists
} from '../common/visitAttachments';
import { startICloudBackup } from '../icloud/icloud';
import { decodeAttach } from '../icloud/iCloudFiles';

// eslint-disable-next-line no-undef
const fs = require('react-native-fs');

export const TIMEOUT_FOR_AUTO_BACKUP = 10 * 1000;


export const toLog = (name, ...params) => {
    if (Object.androidLog === false) {
        return;
    }
    actions.addLog(`${name}`, params);
    const isError = name?.indexOf('error') >= 0 || name?.indexOf('Error') >= 0;
    if (isError) {
        console.error(`%c${name}`, 'color: #882200', ...params);

    } else {
        console.log(`%c${name}`, 'color: #229900', ...params);
    }
};


export const getWebClientId = () => {
    const client = googleServices?.client;
    if (!client || !Array.isArray(client)) {
        throw new Error(`Cannot get client id: No client section`);
    }

    let clientId = null;
    client.some(({ oauth_client }) => {
        if (!oauth_client || !Array.isArray(oauth_client)) {
            return false;
        }
        const found = oauth_client.find(({ client_type }) => String(client_type) === '3');
        if (found) {
            clientId = found.client_id;
        }
        return !!found;
    });

    return clientId ?? '';
};

export const getGoogleDriveConnectionInfo = async () => {
    if (Platform.OS === 'web') {
        toLog('getGoogleDriveConnectionInfo', 'Web platform detected, skipping Google Drive connection info');
        return { type: 'web' };
    }
    const scopes = [ 'https://www.googleapis.com/auth/drive.appdata' ];
    GoogleSignin.configure({
        scopes,
        offlineAccess: true,
        webClientId: getWebClientId(),
    });
    return await GoogleSignin.signInSilently();
};

export const getDriveAccessToken = async (logon = true, onCancel) => {
    try {
        const scopes = [ 'https://www.googleapis.com/auth/drive.appdata' ];
        GoogleSignin.configure({
            scopes,
            offlineAccess: true,
            webClientId: getWebClientId(),
        });

        let data = await GoogleSignin.signInSilently();
        toLog('Token', 'Silent', {
            silent: data?.type ?? 'no data',
            data,
            logon,
        });
        if (data?.type !== 'success' && logon && !backupInfoProcessing.disableInteractive) {
            toLog('Silent unsuccessful', 'GoogleDrive login - get token');
            backupInfoProcessing.disableInteractive = true;
            data = await GoogleSignin.signIn();
            if (data?.type !== 'cancelled') {
                backupInfoProcessing.disableInteractive = false;
            } else {
                onCancel?.();
                setTimeout(() => {
                    backupInfoProcessing.disableInteractive = false;
                }, 2000);
            }
            toLog('Token', 'GoogleDrive login - get token', { signIn: data ?? 'no data' });
        }

        const tokens = await GoogleSignin.getTokens();
        toLog('Token', 'Google Drive access token', { tokens });
        return tokens?.accessToken;
    } catch (e) {
        toLog('Token', 'Error configuring Google Signin', e);
        return null;
    }
};

export const googleErrorDecoder = (error) => {
    if (!error) {
        return Object.R('errors.unknownError', {});
    }
    let errors;
    if (Array.isArray(error?.errors)) {
        errors = error.errors.map((e) => `google.${e?.domain ?? ''}.${e?.reason ?? ''}`.trim())
            ?.filter(e => e !== 'google..')
            ?.map(e => Object.R(e))
            .join(', ');
    } else if (typeof error === 'object') {
        errors = error?.message || `Error object ${error?.toString()}`;
    } else {
        errors = `Error string ${error?.toString()}`;
    }
    return errors || Object.R('errors.unknownError', {});
};

export const uploadRawFile = async ({
    data,
    fileName,
    accessToken,
}) => {
    const token = accessToken || await getDriveAccessToken();
    if (!token) {
        throw new Error(Object.R('errors.noAccessGoogleDrive'));
    }

    const metadata = {
        name: fileName,
        parents: [ 'appDataFolder' ], // This uploads to appDataFolder
    };

    const body =
        `--${fileName}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        `${JSON.stringify(metadata)}\r\n` +
        `--${fileName}\r\n` +
        `Content-Type: application/octet-stream\r\n\r\n` +
        `${typeof data === 'string' ? data : JSON.stringify(data)}\r\n` +
        `--${fileName}--\r\n`;

    const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': `multipart/related; boundary="${fileName}"`,
    };

    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers,
        body,
    });

    if (!response.ok) {
        let msg;
        try {

            msg = await response.json();
        } catch (e) {
            msg = e?.toString();
        }
        toLog('UploadRawFile', 'Error uploading raw file', {
            response,
            msg,
        });
        throw new Error(Object.R('errors.backupError', { addInfo: Object.R(googleErrorDecoder(msg?.error)) }));
    }
    return await response.json();
};

const uploadFile = async ({
    data,
    profileId,
    accessToken,
    withAttachments,
}) => {
    const fileName = await getFileName(profileId, withAttachments);
    const fileMetadata = {
        name: `${fileName}`,
        mimeType: 'application/json',
        parents: [ 'appDataFolder' ],
    };

    const body =
        `--${profileId}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        `${JSON.stringify(fileMetadata)}\r\n` +
        `--${profileId}\r\n` +
        `Content-Type: application/json\r\n\r\n` +
        `${JSON.stringify(data)}\r\n` +
        `--${profileId}\r\n`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary="${profileId}"`,
    };
    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers,
        body,
    });
    if (!response.ok) {
        const responseText = await response.json();
        actions.addLog('GoogleDrive', {
            1: responseText,
            message: 'Error uploading file to Google Drive',
        });
        toLog('UploadFile', 'Error uploading file', {
            responseText,
            response,
        });
        if (responseText?.error?.status === 'UNAUTHENTICATED') {
            actions.addLog('GoogleDrive', {
                message: 'Google Drive access token is invalid or expired',
                responseText,
            });
            throw new Error(Object.R('errors.noAccessGoogleDrive'));
        }
        throw new Error(Object.R('errors.backupError', { addInfo: googleErrorDecoder(responseText?.error) }));
    }
    return await response.json();
};
const uploadJpeg = async ({
    data,
    fileName,
    accessToken,
}) => {
    const metadata = {
        name: fileName,
        parents: [ 'appDataFolder' ], // This uploads to appDataFolder
    };
    const id = getRandomNumberString(12);

    // Step 4: Create multipart upload data
    const boundary = `-------${id}`;
    const delimiter = `\r\n--${boundary}\r\n`;
    const close_delim = `\r\n--${boundary}--`;

    const metadataString = JSON.stringify(metadata);

    const multipartRequestBody =
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        metadataString +
        delimiter +
        'Content-Type: image/jpeg\r\n' +
        'Content-Transfer-Encoding: base64\r\n\r\n' +
        data +
        close_delim;

    // Step 5: Upload to Google Drive
    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': `multipart/related; boundary="${boundary}"`,
            'Content-Length': multipartRequestBody.length.toString(),
        },
        body: multipartRequestBody,
    });

    const result = await response.json();
    if (response.ok) {
        toLog('Upload', 'Upload successful:', result);
        return result;
    }
    const msg = googleErrorDecoder(result?.error);
    toLog('Upload', 'Upload failed:', msg);
    throw new Error(msg);
};

const uploadFunction = async ({
    inFile,
    outFile,
}, {
    accessToken,
    profileId,
}) => {
    toLog('UF', 'GoogleDrive:uploadFunction', {
        inFile,
        outFile,
        accessToken,
        profileId,
    });
    const data = await fs.readFile(inFile, 'base64');
    return uploadJpeg({
        data,
        accessToken,
        profileId,
        fileName: outFile,
    });
};

export const getAllFiles = async ({
    accessToken,
    logon = true,
}) => {
    const token = accessToken ?? (await getDriveAccessToken(logon))?.accessToken;
    const q = encodeURIComponent('\'appDataFolder\' in parents');
    const fields = encodeURIComponent('files(id,name,size,createdTime)');
    const url = `https://www.googleapis.com/drive/v3/files?q=${q}&spaces=appDataFolder&fields=${fields}`;
    toLog('getAllFiles', {
        token,
        url,
        bearer: `Bearer ${token}`,
    });
    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        const text = await res.json();
        toLog('getAllFiles:error', {
            text,
            message: 'Error getting all files from Google Drive',
        });
        throw new Error(googleErrorDecoder(text?.error));
    }
    return await res.json();
};

export const getFiles = async (profileId, accessToken, filterByDevice = false) => {
    try {
        const token = accessToken ?? (await getDriveAccessToken());
        const data = await getAllFiles({ accessToken: token });
        const fileName = await getFileName(profileId);
        const result = data?.files?.filter(({ name }) => compareFileName({
            fileName1: name,
            fileName2: fileName,
            filterByDevice,
        })); // Array of files {id, name, ...}
        toLog('getFiles', {
            data,
            fileName,
            result,
            profileId,
            filterByDevice,
        });
        return {
            files: data,
            fileName,
            data: result,
        };
    } catch (e) {
        toLog('getFiles error', e);
        throw new Error(e?.message || Object.R('errors.getFilesError', {}));
    }
};

export const removeDuplicatesFromGoogleDrive = async (accessToken) => {
    try {
        const token = accessToken ?? (await getDriveAccessToken(false));
        const filesData = await getAllFiles({
            logon: false,
            accessToken: token,
        });

        const files = filesData?.files || [];
        const dup = getDuplicates(files, 'name');
        for (const d of dup) {
            await deleteGoogleFile(d?.id, token);
        }
    } catch (e) {
        //
    }
};

export const uploadAttachments = async ({
    attachments = [],
    profileId,
    accessToken: token,
    onSuccess,
    onError,
    onComplete,
    onProgress,
}) => {
    try {
        let toUpload = [ ...attachments ];
        const accessToken = token ?? (await getDriveAccessToken());
        const { files } = await getFiles(profileId, accessToken);
        if (files && Array.isArray(files?.files)) {
            toUpload = toUpload.filter((attachment) => {
                const fileName = getAttachFileName(profileId, attachment?.path?.split?.('/')
                    ?.pop
                    ?.());
                return !files?.files?.some?.((file) => {
                    const fn = file?.name;
                    return fn === fileName;
                });
            });
        }
        toUpload = toUpload?.map((attachment) => ({
            inFile: attachment.path,
            outFile: getAttachFileName(profileId, attachment?.path?.split?.('/')
                ?.pop
                ?.()),
        }));
        toLog('uploadAttachment', 'GoogleDrive:files', {
            files,
            attachments,
            profileId,
            toUpload,
        });
        if (toUpload.length === 0) {
            toLog('uploadAttachment', 'No attachments to upload');
            const res = {
                successful: [],
                failed: [],
                total: 0,
            };
            onComplete?.(res);
            onSuccess?.(res);
            return;
        }
        toUpload = toUpload.filter(Boolean);
        toLog('uploadAttachment', 'GoogleDrive:toUpload', { filesToUpload: toUpload });
        const fileUpload = new FileUploadService();
        const state = getCurrentState();
        const {
            backupAttachmentsWifi = true,
        } = state?.prefs?.data ?? {};
        const res = await fileUpload.uploadFiles({
            files: toUpload,
            wifiOnly: backupAttachmentsWifi,
            uploadFunction,
            uploadParams: {
                profileId,
                accessToken,
            },
            callbacks: {
                onComplete: (result) => {
                    onComplete?.(result);
                    toLog('uploadAttachment', 'complete', { attachmentsUploadCompleted: result });
                    // showLogModal('Completed attachments upload', result);
                },
                onError: (file, error) => {
                    onError?.(error);
                    toLog('uploadAttachment', 'error', {
                        attachError: error,
                        file,
                    });
                },
                onProgress: (completed, total, percentage) => {
                    onProgress?.(completed, total, percentage);
                    toLog('uploadAttachment', 'progress', {
                        attachmentsUploadProgress: {
                            completed,
                            total,
                            percentage,
                        },
                    });
                },
                onSuccess: (result) => {
                    onSuccess?.(result);
                    toLog('uploadAttachment', 'success', { attachmentsUploadSuccess: result });
                },
            },
        });
        toLog('uploadAttachment', 'end', { attachmentsUploadResult: res });
    } catch (e) {
        toLog('uploadAttachment', 'exception', { uploadAttachmentsError: e });
    }
};

export const deleteGoogleFile = async (fileId, accessToken) => {
    const token = accessToken ?? (await getDriveAccessToken());
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        actions.addLog('GoogleDrive', {
            3: await res.text(),
            message: 'Error deleting file from Google Drive',
        });
        throw new Error(await res.text());
    }
};

export const googleBackup = async ({
    data,
    profileId,
    withAttachments,
    interactive,
}) => {
    const accessToken = await getDriveAccessToken(typeof interactive === 'boolean' ? interactive : backupInfoProcessing.interactive);
    backupInfoProcessing.interactive = false;
    toLog('googleBackup', 'started', {
        profileId,
        accessToken,
    });

    try {
        const answer = await uploadFile({
            data,
            profileId,
            accessToken,
            withAttachments,
        });
        const answerId = answer?.id;
        const fileList = await getFiles(profileId, accessToken);
        toLog('googleBackup', 'upload file result', {
            answer,
            fileList,
            profileId,
        });
        const fileName2 = await getFileName(profileId, false);
        fileList?.data?.forEach(({
            id,
            name,
        }) => {
            if (id !== answerId && !(compareFileName({
                fileName1: name,
                fileName2,
                filterByDevice: false,
            }) && name !== fileName2)) {
                deleteGoogleFile(id, accessToken)
                    .then(() => {
                        //
                    })
                    .catch((e) => {
                        toLog('googleBackup', 'Error deleting file', { e });
                    });
            }
        });
        return answer;
    } catch (e) {
        actions.addLog('GoogleDrive', {
            4: e,
            message: 'Error Google backup',
        });
        toLog('googleBackup', 'exception', { e });
        throw new Error(e);
    }
};

export const getDriveInfo = async (accessToken) => {
    const token = accessToken ?? (await getDriveAccessToken());

    const res = await fetch('https://www.googleapis.com/drive/v3/about?fields=storageQuota', {
        headers: { Authorization: `Bearer ${token}` },
        method: 'GET',
    });
    toLog('googleDriveInfo', 'result', { res });
    if (!res.ok) {
        const text = await res.json();
        toLog('googleDriveInfo', 'error', {
            9: text,
            message: `Error getting Google Drive info <${text}>`,
        });
        throw new Error(text);
    }
    const result = await res.json();
    toLog('googleDriveInfo', 'info', { result });
    const { storageQuota } = result;

    // Calculate free space
    const limit = storageQuota.limit ? parseInt(storageQuota.limit) : null;
    const usageInDrive = parseInt(storageQuota.usageInDrive || 0);
    const usageInTrash = parseInt(storageQuota.usageInTrash || 0);
    const totalUsage = parseInt(storageQuota.usage || 0);
    return {
        limit,
        usageInDrive,
        usageInTrash,
        totalUsage,
        freeSpace: limit ? limit - totalUsage : null,
        usedSpace: totalUsage,
        usedSpaceInDrive: usageInDrive,
        usedSpaceInTrash: usageInTrash,
    };
};

const restoreFileId = async ({
    fileId,
    accessToken,
    hashKey,
    profileId,
}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        toLog('restoreFileId', 'error', {
            5: await res.text(),
            message: 'Error restoring file from Google Drive',
        });
        throw new Error(await res.text());
    }

    const result = await res.json();
    let content;
    try {
        content = JSON.parse(result);
    } catch (e) {
        toLog('restoreFileId', 'Error parsing JSON', { e });
        throw new Error(Object.R('errors.restoreFailed', {}));
    }

    if (content?.visits && content?.profileId === profileId) {
        if (hashKey) {
            content.decryptedVisits = decrypt(content.visits, hashKey);
        }
    } else {
        content.visits = null;
    }
    return content;
};

export const renameFile = async ({
    fileId,
    newName,
    accessToken,
}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newName }),
    });
    if (!res.ok) {
        let msg;
        try {
            msg = await res.json();
            msg = googleErrorDecoder(msg?.error);
        } catch (e) {
            msg = res.statusText;
        }
        toLog('renameFile', `result ${fileId}`, {
            msg,
            message: 'Error renaming file in Google Drive',
        });
        throw new Error(msg);
    }
    return await res.json();
};

const getFileIdByName = async (fileName, accessToken) => {
    try {
        const params = new URLSearchParams({
            q: `name='${fileName}' and parents in 'appDataFolder'`,
            spaces: 'appDataFolder',
        });

        const response = await fetch(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });


        if (response.ok) {
            const data = await response.json();
            toLog('getFileIdByName', 'ok', {
                result: data.files && data.files.length > 0 ? data.files[0].id : null,
                data,
            });
            // Return file ID if found, null if not found
            return data.files && data.files.length > 0 ? data.files[0].id : null;
        }
        try {
            let msg = await response.json();
            msg = googleErrorDecoder(msg?.error);
            toLog('getFileIdByName', msg);
            return null;
        } catch (e) {
            toLog('getFileIdByName', 'Error parsing error response', { e });
        }
    } catch (error) {
        console.error('Network error:', error);
        return null;
    }
};
export const changePendingStatus = async ({
    profileId,
    pending,
    accessToken,
}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    toLog('changePendingStatus', {
        profileId,
        pending,
        accessToken,
    });
    const fileName = await getFileName(profileId, !pending);

    const fileId = await getFileIdByName(fileName, token);
    if (!fileId) {
        toLog('changePendingStatus', 'no file, return');
        return null;
    }
    const newName = await getFileName(profileId, pending);
    return renameFile({
        fileId,
        newName,
        accessToken: token,
    });
};

const readFileById = async ({
    fileId,
    accessToken,
}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    const res = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        let msg = await res.json();
        msg = googleErrorDecoder(msg?.error);
        toLog('readFileById', `read error file ${fileId}`, {
            msg,
            message: 'Error reading file by ID from Google Drive',
        });
        throw new Error(msg);
    }
    return await res.json();
};

export const restoreAttachments = async ({
    list,
    profileId,
    accessToken,
    attachments,
}) => {
    if (!USE_ATTACHMENTS) {
        return '';
    }
    const files = list?.files?.files ?? [];
    toLog('restoreAttachment', 'start', {
        files,
        list,
        profileId,
        accessToken,
        attachments,
    });

    const profileFiles = files
        ?.filter(({ name }) => name?.startsWith?.(getAttachFileName(profileId, '')))
        ?.map((file) => ({
            ...file,
            name: file?.name?.replace?.(getAttachFileName(profileId, ''), ''),
        }));
    const toDownload = [];
    for (const file of profileFiles) {
        const fileName = convertToDocPath(file?.name);
        const size = await isExists(fileName);
        if (size > 10) {
            toLog('restoreAttachments', 'File already exists', {
                size,
                fileName,
            });
            continue;
        }
        toDownload.push(file);
    }
    toLog('restoreAttachments', 'toDownload', toDownload);
    const token = accessToken ?? (await getDriveAccessToken());
    const result = [];
    for (const file of toDownload) {
        try {
            const res = await fs.downloadFile({
                headers: { Authorization: `Bearer ${token}` },
                fromUrl: `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(file.id)}?alt=media`,
                toFile: `${fs.DocumentDirectoryPath}/${file.name}`,
            }).promise;

            if (res?.statusCode !== 200) {
                toLog('restoreAttachments', 'Error downloading file', {
                    statusCode: res?.statusCode,
                    file,
                });
                result.push({
                    name: file.name,
                    result: 'error',
                    error: `Download failed with status ${res?.statusCode}`,
                });
                continue;
            }
            result.push({
                name: file.name,
                result: 'ok',
            });
        } catch (e) {
            toLog('restoreAttachments', 'exception restoring', {
                e,
                file,
            });
            result.push({
                name: file.name,
                result: 'error',
                error: e?.toString(),
            });
        }
    }

    toLog('restoreAttachments', 'end', {
        files,
        profileFiles,
        toDownload,
        attachments,
    });

    return result;
};

export const googleRestore = async ({
    profileId,
    hashKey,
}) => {
    const accessToken = await getDriveAccessToken();

    return new Promise((resolve, reject) => {
        getFiles(profileId, accessToken)
            .then((list) => {
                if (!list?.data || !Array.isArray(list?.data) || list?.data?.length === 0) {
                    resolve({
                        message: Object.R('warning.noDataForRestore', {}),
                        data: null,
                    });
                    return;
                }
                const files = list.data;
                restoreAttachments({
                    list,
                    profileId,
                    accessToken,
                });
                if (files?.length > 1) {
                    selectOptions({
                        title: 'titles.selectBackupForRestore',
                        data: files?.map(({
                            id,
                            createdTime,
                        }) => ({
                            id,
                            name: format(toDate(createdTime), 'dd-MM-yyyy HH:mm'),
                        })),
                        onSelect: (item) => {
                            restoreFileId({
                                hashKey,
                                profileId,
                                fileId: item.id,
                                accessToken,
                            })
                                .then((res) => {
                                    resolve({
                                        message: Object.R('success.restoreCompleted', {}),
                                        data: res,
                                    });
                                })
                                .catch((err) => {
                                    actions.addLog('GoogleDrive', {
                                        7: err,
                                        message: 'Error restoring file from Google Drive',
                                    });

                                    toLog('Error Google restore', { err });
                                    reject({
                                        message: Object.R('errors.restoreFailed', {}),
                                        data: null,
                                    });
                                });
                        },
                    });
                } else {
                    restoreFileId({
                        hashKey,
                        profileId,
                        fileId: files[0]?.id,
                        accessToken,
                    })
                        .then((res) => {
                            resolve({
                                message: Object.R('success.restoreCompleted', {}),
                                data: res,
                            });
                        })
                        .catch((err) => {
                            actions.addLog('GoogleDrive', {
                                8: err,
                                message: 'Error restoring file from Google Drive',
                            });
                            toLog('Error Google restore', { err });
                            reject({
                                message: Object.R('errors.restoreFailed', {}),
                                data: null,
                            });
                        });
                }
            });
    });
};

export const removeUnusedAttachments = async ({
    // attachments = [],
    profileId,
}) => {
    if (!USE_ATTACHMENTS) {
        return;
    }
    const accessToken = await getDriveAccessToken();
    const { files } = await getFiles(profileId, accessToken);

    const att = await getDeletedAttaches();
    const attachesForRemove = att?.map(name => extractFileNameFromFullPath(name));
    if (attachesForRemove?.length) {
        try {

            const toDelete = [];
            const decodedFiles = files?.files?.map?.(f => ({
                ...f,
                name: decodeAttach(f?.name, profileId),
            }));
            attachesForRemove?.forEach?.(name => {
                const found = decodedFiles?.find?.(f => f?.name === name);
                if (found) {
                    toDelete.push(found);
                }
            });

            for (const file of toDelete) {
                try {
                    await deleteGoogleFile(file.id, accessToken);
                } catch (e) {
                    toLog('Error deleting file', { e });
                }
            }
            if (CLEAR_DELETED_ATTACHES_AFTER_BACKUP) {
                await clearDeletedAttaches();
            }
        } catch (e) {
            toLog('error', 'delete attaches error', e);
        }
    }
    await removeDuplicatesFromGoogleDrive(accessToken);

};

export const getDeviceNameFromFile = async (file, token) => {
    if (!file) {
        toLog('getDeviceNameFromFile', 'No file provided');
        return '';
    }
    const currentDeviceId = await getDeviceId();
    const parts = file?.name?.split?.(PARTS_DELIMITER);
    toLog('getDeviceNameFromFile', {
        file,
        parts,
        currentDeviceId,
    });
    if (!parts || parts.length < 2) {
        toLog('getDeviceNameFromFile', 'Invalid file name format', { fileName: file?.name });
        return '';
    }
    if (parts[1] === currentDeviceId) {
        return (await DeviceInfo.getDeviceName()) ?? '';
    }
    const accessToken = token ?? (await getDriveAccessToken());
    const { devicesInfo: devices } = await getDevicesData({ accessToken });
    const deviceId = parts[1];
    const device = devices?.[deviceId];
    const model = device?.deviceModel ?? '';
    const name = device?.deviceName ?? '';
    const result = (`${name} (${model})`)?.trim?.() ?? '';
    toLog('getDeviceNameFromFile result', {
        devices,
        parts,
        currentDeviceId,
        deviceId,
        device,
        model,
        name,
        result,
    });
    return result === '()' ? '' : result;

};

export const getGoogleDriveBackupInfo = async (profileId, logon = false) => {
    const token = await getDriveAccessToken(logon);
    if (!token) {
        toLog('getGoogleDriveBackupInfo', 'No access token for Google Drive', {
            profileId,
            logon,
            token,
        });
        throw new Error('No access token for Google Drive');
    }

    const devices = await getDevicesData({ accessToken: token });

    const list = await getFiles(profileId, token);
    const attachFileName = getAttachFileName(profileId, '');
    const myFiles = (list?.files?.files ?? [])?.filter?.(({ name }) => name?.startsWith(attachFileName)) ?? [];

    const attachSize = myFiles.reduce(
        (acc, file) => acc + (isNaN(file?.size) ? 0 : Number(file?.size ?? 0) || 0),
        0,
    );

    const parsedList = list?.data?.map((file) => {
        const parts = file?.name?.split?.(PARTS_DELIMITER);
        const deviceId = parts?.[1] ?? '';
        return {
            ...file,
            size: (isNaN(file?.size) ? 0 : Number(file?.size ?? 0) || 0) + attachSize,
            device: {
                deviceId,
                deviceName: devices?.devicesInfo?.[deviceId]?.deviceName ?? '',
                deviceModel: devices?.devicesInfo?.[deviceId]?.deviceModel ?? '',
            },
        };
    }) ?? [];


    const backups = sortBy(parsedList, (item) => new Date(item.createdTime).getTime())
        .reverse();
    const lastBackup = backups?.[0] ?? null;

    // eslint-disable-next-line no-constant-binary-expression
    const backupSize = myFiles.reduce(
        (acc, file) => acc + (isNaN(file?.size) ? 0 : Number(file?.size ?? 0) || 0),
        isNaN(lastBackup?.size) ? 0 : Number(lastBackup?.size ?? 0) || 0,
    );
    toLog('GoogleDrive:backups', {
        backups,
        list,
        myFiles,
        attachSize,
        devices,
        profileId,
    });

    return {
        lastBackup: lastBackup ? new Date(lastBackup.createdTime) : null,
        lastBackupFile: lastBackup,
        backups,
        files: list?.files,
        myFiles,
        attachFileName,
        backupSize,
        attachSize,
    };
};

export const startGoogleBackup = async ({
    onSuccess,
    onError,
    interactive,
} = {}) => {
    toLog('GoogleDrive:startGoogleBackup', {
        onSuccess,
        onError,
    });
    const state = getCurrentState();
    const profileId = state?.user?.info?.userId ?? '';
    const netStatus = await getNetworkStatus();
    const {
        googleCloudEnabled = false,
        backupAttachmentsWifi = true,
        backupVisitsAttachments = true,
        backupMyDocs = true,
    } = state?.prefs?.data ?? {};
    toLog('pref data wifi', state?.prefs?.data);
    const { backupProcessing } = state?.forms?.values ?? {};
    if (googleCloudEnabled === false) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveBackupDisabled'));
        toLog('GoogleDrive:backup', {
            message: 'Google Drive backup disabled',
            data: state?.prefs?.data,
            state,
        });
        return;
    }
    if (backupProcessing) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveBackupAlreadyStarted'));
        toLog('GoogleDrive:backup', 'Google Drive backup already in progress');
        return;
    }
    try {
        actions.setFormValue('backupProcessing', true);
        setBackupError('');
        const backupData = await getFullBackupData();
        toLog('Start Google backup', {
            googleCloudEnabled,
            backupMyDocs,
            backupAttachmentsWifi,
            backupVisitsAttachments,
            data: state?.prefs?.data,
            backupData,
        });
        await setDeviceData();

        toLog('Google backup visits:', backupData);
        if (!backupData?.encryptedData || !backupData?.profileId) {
            throw new Error('errors.noDataOrProfileForBackup');
        }
        const { attachments } = backupData?.data ?? {};
        const answer = await googleBackup({
            interactive,
            data: backupData?.backupData,
            profileId: backupData?.profileId,
            withAttachments: !!attachments?.length,
        });
        toLog('GoogleDrive:backupResult', answer);
        toLog('GoogleDrive:backupAttachments', attachments);
        if (attachments?.length) {
            toLog('wifi start', {
                netStatus,
                backupAttachmentsWifi,
            });
            if ((backupAttachmentsWifi && netStatus?.isWifi) || !backupAttachmentsWifi) {
                toLog('wifi', {
                    backupAttachmentsWifi,
                    netStatus,
                });

                await uploadAttachments({
                    attachments,
                    profileId: backupData?.profileId,
                    onError: (error) => {
                        toLog('Error uploading file', { error });
                        setLastBackupState({
                            state: 'error',
                            error: Object.R('errors.googleDriveBackupAttachmentsError', { addInfo: error?.message ?? '' }),
                        });
                        setBackupError(Object.R('errors.googleDriveBackupAttachmentsError', { addInfo: error?.message ?? '' }));
                        onError?.(error);
                    },
                    onComplete: async (result) => {
                        toLog('Google backup attachments completed', result);
                        if (result?.failed?.length) {
                            actions.setFormValue(
                                'googleBackupProcessMessages',
                                Object.R('errors.googleDriveBackupAttachmentsFailed', { count: result?.failed?.length }),
                            );
                            setLastBackupState({
                                state: 'error',
                                error: Object.R('errors.googleDriveBackupAttachmentsFailed', {
                                    count: result?.failed?.length,
                                }),
                            });
                            await changePendingStatus({
                                profileId,
                                pending: false,
                            });
                            actions.setFormValue('backupProcessing', false);
                            onError?.();
                        } else {
                            setLastBackupState({
                                state: 'success',
                                error: '',
                            });
                            await changePendingStatus({
                                profileId,
                                pending: false,
                            });
                            onSuccess?.();
                        }
                        actions.setFormValue('backupProcessing', false);
                        console.log('Google backup attachments completed - remove unused', attachments);
                        await removeUnusedAttachments({
                            // attachments,
                            profileId: backupData?.profileId,
                        });
                    },
                });
            } else {
                toLog('wifi Not need attachments', {
                    backupAttachmentsWifi,
                    netStatus,
                });
                setLastBackupState({
                    state: 'success',
                    error: '',
                });
                await changePendingStatus({
                    profileId,
                    pending: false,
                });
                actions.setFormValue('backupProcessing', false);
                onSuccess?.();
            }
        } else {
            await removeUnusedAttachments({
                // attachments,
                profileId: backupData?.profileId,
            });
            actions.setFormValue('backupProcessing', false);
            setLastBackupState({
                state: 'success',
                error: '',
            });
            onSuccess?.();
        }
    } catch (e) {
        toLog('Error startGoogleBackup', e);
        const error = Object.R('errors.googleDriveBackupError', { addInfo: googleErrorDecoder(e) ?? '' });
        setLastBackupState({
            state: 'error',
            error,
        });
        setBackupError(error);

        actions.setFormValue('backupProcessing', false);
        onError?.(e);
    }
};

export const doAutoBackup = async (data) => {
    const state = getCurrentState();
    const {
        googleCloudEnabled = false,
        iCloudEnabled = false,
    } = state?.prefs?.data ?? {};

    loadBackupStates()
        .then((r) => {
            toLog('===doAutoBackup:states', r);
        })
        .catch((error) => {
            toLog('Error loading backup states', error);
        });

    setTimeout(async () => {
        toLog('GoogleDrive:doAutoBackup', data);
        if (autoBackup?.started) {
            toLog('autoBackup already started');
            return;
        }
        try {
            autoBackup.started = true;
            const state = getCurrentState();
            const { history } = await loadBackupStates();
            toLog('===doAutoBackup:history', history);
            const backupFrequency = state?.db?.backupFrequency ?? 86400;
            const now = Date.now();
            const lastBackup = history?.[0]?.date;
            const nextBackup = lastBackup + +backupFrequency * 1000;
            if (nextBackup < now || !lastBackup) {
                toLog('Got next backup now', nextBackup);
                !!googleCloudEnabled && startGoogleBackup({
                    interactive: true,
                    onSuccess: () => {
                        setBackupError('');
                    },
                })
                    .then(() => console.log('doAutoBackup:Google backup started ok'))
                    .catch((error) => {
                        toLog('doAutoBackup:Error starting Google backup', error);
                    });
                !!iCloudEnabled && startICloudBackup({
                    onSuccess: () => setBackupError(''),
                    onError: e => setBackupError(e),
                })
                    .then(() => toLog('Finish iCloudBackup auto'))
                    .catch(e => toLog('iCloud backup auto error', e));
            } else {
                toLog('doAutoBackup:No need for backup', {
                    nextBackup: format(toDate(nextBackup), 'dd-MM-yyyy HH:mm'),
                    now: format(toDate(now), 'dd-MM-yyyy HH:mm'),
                    lastBackup: format(toDate(lastBackup), 'dd-MM-yyyy HH:mm'),
                    lastBackupMs: lastBackup,
                    nowMs: now,
                    nextBackupMs: nextBackup,
                    backupFrequency,
                });
            }
        } catch (e) {
            toLog('Error in doAutoBackup', e);
        } finally {
            autoBackup.started = false;
        }
    }, TIMEOUT_FOR_AUTO_BACKUP);
    toLog('===doAutoBackup', data);
};

export const getDevicesData = async ({ accessToken } = {}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    toLog('getDevicesData', { token });
    const files = await getAllFiles({ accessToken: token });
    toLog('getDevicesData:files', files);
    try {
        const deviceFile = files?.files?.filter(({ name }) => name === DEVICE_FILE_NAME) ?? [];
        let devicesInfo = {};
        let fileId = null;
        const result = {
            fileId,
            devicesInfo,
        };
        fileId = deviceFile[0]?.id;
        if (fileId) {
            result.fileId = fileId;
            devicesInfo = await readFileById({
                fileId,
                accessToken: token,
            });
            result.devicesInfo = devicesInfo;
        }
        toLog('getDevicesData:result', result);
        return result;
    } catch (e) {
        toLog('read device data error', {
            message: 'no devices info',
            e,
        });
        return null;
    }
};

/**
 * Sets device data in Google Drive.
 * If the device data file already exists, it will be replaced.
 * If it does not exist, a new file will be created.
 * The file will be stored in the appDataFolder of Google Drive.
 * The file name will be 'devices.json' = const DEVICE_FILE_NAME.
 * @param accessToken
 * @returns Promise<{ fileId: string, devicesInfo: {deviceId: {deviceId: string, deviceName: string, deviceModel: string}> }}
 */
export const setDeviceData = async ({ accessToken } = {}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    const deviceData = {
        deviceId: (await getDeviceId()),
        deviceName: DeviceInfo.getDeviceNameSync(),
        deviceModel: DeviceInfo.getModel(),
    };
    let devicesInfo, fileId;
    try {

        const answer = await getDevicesData({ accessToken: token });
        devicesInfo = answer?.devicesInfo;
        fileId = answer?.fileId;

        toLog('setDeviceData', {
            fileId,
            devicesInfo,
            deviceData,
        });
    } catch (e) {
        toLog('setDevicesData:error:getDevicesData', e);
        throw new Error(e);
    }

    devicesInfo[deviceData.deviceId] = deviceData;

    toLog('setDeviceData:devicesInfo', devicesInfo);

    try {
        const file = await uploadRawFile({
            data: devicesInfo,
            accessToken: token,
            fileName: DEVICE_FILE_NAME,
        });

        toLog('setDeviceData:upload', {
            file,
            devicesInfo,
        });

        if (fileId) {
            await deleteGoogleFile(fileId, token);
        }

        return file;
    } catch (e) {
        toLog('setDeviceData:uploadError', { e });
        throw new Error(e?.message ?? '');
    }
};

export const renameBackupToDevice = async ({
    backup,
    profileId,
    deviceId,
    accessToken,
}) => {
    const token = accessToken ?? (await getDriveAccessToken());
    if (!backup?.id) {
        toLog('renameBackupToDevice', 'No backup file provided');
        throw new Error(Object.R('errors.googleDriveRestoreNotSupported'));
    }
    const newName = await getFileName(profileId, false);
    toLog('renameBackupToDevice', {
        backup,
        profileId,
        deviceId,
        newName,
    });
    return renameFile({
        fileId: backup.id,
        newName,
        accessToken: token,
    });
};
export const restoreGoogleBackup = async (backupToRestore, renameToCurrentDevice = true) => {
    const state = store?.getState?.();
    const { backupProcessing } = state?.forms?.values ?? {};
    const profileId = state?.user?.info?.userId ?? '';
    const hashKey = state?.user?.info?.hashKey ?? '';
    const deviceId = await getDeviceId();
    if (backupProcessing) {
        toLog('restoreGoogleBackup', 'Backup already in progress');
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveRestoreAlreadyStarted'));
        return;
    }

    try {
        backupInfoProcessing.disableBackup = true;
        actions.setFormValue('backupProcessing', true);
        toLog('GoogleDrive:restoreBackup', {
            backupToRestore,
            renameToCurrentDevice,
        });
        // return;

        const currentData = await getFullBackupData({ disableLog: false });
        if (!backupToRestore?.id) {
            toLog('GoogleDrive:restoreBackup no file', backupToRestore);
            throw new Error(Object.R('errors.googleDriveRestoreNotSupported'));
        }
        const token = await getDriveAccessToken();
        await setDeviceData({ accessToken: token });

        if (!token) {
            actions.setFormValue('googleBackupProcessMessages', Object.R('errors.noAccessGoogleDrive'));
            actions.setFormValue('backupProcessing', false);
            throw new Error(Object.R('errors.noAccessGoogleDrive'));
        }

        const file = await readFileById({
            fileId: backupToRestore?.id,
            accessToken: token,
        });

        toLog('GoogleDrive:file', { file });

        const data = await restoreData({
            file,
            profileId,
            hashKey,
        });

        const files = (await getFiles(profileId, token))?.files?.files ?? [];
        const attachments = files?.filter?.(({ name }) => name?.startsWith?.(getAttachFileName(profileId, ''))) ?? [];

        toLog('GoogleDrive:restoreFile start', {
            file,
            profileId,
            data,
            attachments,
            files,
        });

        try {
            const attachmentResult = await restoreAttachments({
                list: { files: { files } },
                profileId,
                accessToken: token,
                attachments: currentData?.data?.attachments ?? [],
            });

            toLog('GoogleDrive:restoreAttachments', attachmentResult);
            toLog('GoogleDrive:restoreFiles');
        } catch (attachmentError) {
            toLog('Error restoring attachments', attachmentError);
        }

        if (renameToCurrentDevice) {
            await renameBackupToDevice({
                backup: backupToRestore,
                profileId,
                deviceId,
                accessToken: token,
            });
            const allBackups = await getFiles(profileId, token, true);
            const deviceBackups = allBackups?.data?.filter?.((item) => item?.id !== backupToRestore?.id);
            for (const backup of deviceBackups) {
                await deleteGoogleFile(backup?.id, token);
            }
        }
    } catch (error) {
        toLog('Error getting Google Drive backup info', error);
        throw error;
    } finally {
        backupInfoProcessing.disableBackup = false;
        actions.setFormValue('backupProcessing', false);
    }
};
